/**
 * Inventory — page content (CES standard)
 * The selected Outpost's assets grouped by rack, ordered by elevation (top
 * of rack first). Rendered inside CapacityManagement's per-asset tab; the
 * Outpost selector lives there. Rack collapse state lives in the uiStore
 * so it survives navigation.
 */

import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/atoms/Button';
import { AssetCard } from '../components/organisms/AssetCard';
import { RackPanoramic } from '../components/organisms/RackPanoramic';
import { useAssets } from '../hooks/useAssets';
import { useUiStore } from '../stores/uiStore';
import type { AssetSummary } from '../types/outposts';

interface RackGroup {
  rackId: string;
  assets: AssetSummary[];
}

/** Stable fallback — Zustand v5 selectors must return stable references. */
const NO_EXPANDED_RACKS: string[] = [];

function groupByRack(assets: AssetSummary[]): RackGroup[] {
  const racks = new Map<string, AssetSummary[]>();
  for (const asset of assets) {
    const list = racks.get(asset.rackId) ?? [];
    list.push(asset);
    racks.set(asset.rackId, list);
  }
  return [...racks.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([rackId, rackAssets]) => ({
      rackId,
      // Top of rack first
      assets: [...rackAssets].sort((a, b) => (b.rackElevation ?? 0) - (a.rackElevation ?? 0)),
    }));
}

export function Inventory() {
  const { outpostId } = useParams<{ outpostId: string }>();

  const { assets, instancesByAsset, loading, error } = useAssets(outpostId);

  const expandedRacks = useUiStore(
    (state) => state.expandedRacks[outpostId ?? ''] ?? NO_EXPANDED_RACKS,
  );
  const toggleRack = useUiStore((state) => state.toggleRack);
  const setRacksExpanded = useUiStore((state) => state.setRacksExpanded);

  // Only COMPUTE assets are slottable. The backend returns every asset type
  // (storage, etc.); those have no capacity to manage and are hidden here so
  // the inventory shows compute only.
  const racks = useMemo(
    () => groupByRack(assets.filter((asset) => asset.assetType === 'COMPUTE')),
    [assets],
  );

  const allCollapsed = racks.every((rack) => !expandedRacks.includes(rack.rackId));
  const toggleAllRacks = () => {
    if (!outpostId) return;
    setRacksExpanded(outpostId, allCollapsed ? racks.map((rack) => rack.rackId) : []);
  };

  if (loading) return <p className="py-8 text-center text-gray-500">Loading assets…</p>;
  if (error) return <p className="py-8 text-center text-red-600">{error}</p>;

  return (
    <section aria-label="Racks">
      {racks.length > 1 && (
        <div className="mb-3 flex justify-end">
          <Button variant="secondary" small data-testid="toggle-all-racks" onClick={toggleAllRacks}>
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </Button>
        </div>
      )}
      {racks.map((rack) => {
        const collapsed = !expandedRacks.includes(rack.rackId);
        return (
          <div key={rack.rackId} className="mb-8" data-testid={`rack-${rack.rackId}`}>
            <h3 className="mb-2 text-lg font-bold text-gray-900">
              <button
                type="button"
                className="flex w-full cursor-pointer items-center gap-2 py-1 text-left text-lg font-bold text-gray-900"
                aria-expanded={!collapsed}
                data-testid={`toggle-rack-${rack.rackId}`}
                onClick={() => outpostId && toggleRack(outpostId, rack.rackId)}
              >
                <ChevronRight
                  size={16}
                  aria-hidden="true"
                  className={`text-gray-400 transition-transform ${collapsed ? '' : 'rotate-90'}`}
                />
                Rack ID: {rack.rackId}
                <span className="ml-2 text-xs font-normal text-gray-500">
                  {rack.assets.length} asset{rack.assets.length === 1 ? '' : 's'}
                </span>
              </button>
            </h3>
            <RackPanoramic assets={rack.assets} />
            {!collapsed && (
              <div className="flex flex-col gap-3">
                {rack.assets.map((asset) => (
                  <AssetCard
                    key={asset.assetId}
                    asset={asset}
                    outpostId={outpostId ?? ''}
                    instances={
                      instancesByAsset === null
                        ? null
                        : (instancesByAsset.get(asset.assetId) ?? [])
                    }
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
